import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2, Play, Download } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { api, imageUrl, fileUrl } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const item = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const CARD_LABELS = {
  internal: "Read Article",
  external: "Read Article",
  video: "Watch Video",
  resource: "Download",
};

// Prominent, reusable hero card for the newest/featured briefing.
const FeaturedInsightCard = ({ post }) => {
  const label = post.tag === "Legal & Regulatory Update" ? "Read the Briefing" : "Read Article";
  const content = (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[340px] overflow-hidden bg-[#E4E4DE]">
        {post.cover_image_id ? (
          <img
            src={imageUrl(post.cover_image_id)}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-forest/20" />
        )}
      </div>
      <div className="p-8 lg:p-12 flex flex-col justify-center bg-charcoal text-canvas">
        <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-gold">
          {post.tag}
          {post.display_date && <span className="text-canvas/50"> &middot; {post.display_date}</span>}
        </span>
        <h2 className="mt-4 font-serif text-2xl lg:text-3xl leading-tight">{post.title}</h2>
        {post.summary && (
          <p className="mt-4 font-serif text-base lg:text-lg text-canvas/70 leading-relaxed">
            {post.summary}
          </p>
        )}
        {post.jurisdictions && post.jurisdictions.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {post.jurisdictions.map((j) => (
              <span key={j} className="font-sans text-[9px] tracking-[0.16em] uppercase text-gold/90 border border-gold/40 px-2 py-0.5">
                {j}
              </span>
            ))}
          </div>
        )}
        <span className="mt-8 inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.16em] uppercase text-gold group-hover:translate-x-0.5 transition-transform duration-300">
          {label}
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
        </span>
      </div>
    </div>
  );

  const shared = {
    "data-testid": "insight-featured-card",
    className: "group block border border-charcoal/12 mb-px hover:shadow-[0_10px_40px_rgba(0,0,0,0.12)] transition-shadow duration-400",
  };

  if (post.content_type === "external") {
    return <a href={post.external_url} target="_blank" rel="noopener noreferrer" {...shared}>{content}</a>;
  }
  return <Link to={`/insights/${post.slug}`} {...shared}>{content}</Link>;
};

const InsightCard = ({ post }) => {
  const shared = {
    variants: item,
    initial: "hidden",
    whileInView: "visible",
    viewport: { once: true, margin: "0px 0px -60px 0px" },
    "data-testid": `insight-card-${post.id}`,
    className: "group bg-canvas flex flex-col hover:bg-forest transition-colors duration-400",
  };
  const isVideo = post.content_type === "video";
  const isResource = post.content_type === "resource";
  const Icon = isResource ? Download : ArrowUpRight;

  const inner = (
    <>
      <div className="relative aspect-[16/9] overflow-hidden">
        {post.cover_image_id ? (
          <img
            src={imageUrl(post.cover_image_id)}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-[#E4E4DE] group-hover:bg-forest/60 transition-colors duration-400" />
        )}
        {isVideo && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-charcoal/60 text-canvas backdrop-blur-sm group-hover:bg-gold group-hover:text-charcoal transition-colors duration-400">
              <Play className="w-5 h-5 ml-0.5" strokeWidth={1.6} />
            </span>
          </span>
        )}
      </div>
      <div className="p-8 lg:p-10 flex flex-col flex-1">
        <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-gold">
          {post.tag}
          {post.display_date && (
            <span className="text-charcoal/45 group-hover:text-canvas/60 transition-colors duration-400"> &middot; {post.display_date}</span>
          )}
        </span>
        <h2 className="mt-5 font-serif text-xl lg:text-2xl text-charcoal group-hover:text-canvas leading-snug transition-colors duration-400">
          {post.title}
        </h2>
        {post.summary && (
          <p className="mt-4 font-serif text-base text-charcoal/60 group-hover:text-canvas/70 leading-relaxed flex-1 transition-colors duration-400">
            {post.summary}
          </p>
        )}
        <span
          data-testid="insight-read-more"
          className="mt-8 inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.16em] uppercase text-forest group-hover:text-gold transition-colors duration-400"
        >
          {CARD_LABELS[post.content_type] || "Read Article"}
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        </span>
      </div>
    </>
  );

  if (post.content_type === "external") {
    return (
      <motion.a href={post.external_url} target="_blank" rel="noopener noreferrer" {...shared}>
        {inner}
      </motion.a>
    );
  }
  if (isResource) {
    return (
      <motion.a href={fileUrl(post.resource_file_id)} target="_blank" rel="noopener noreferrer" {...shared}>
        {inner}
      </motion.a>
    );
  }
  return (
    <motion.div {...shared}>
      <Link to={`/insights/${post.slug}`} className="flex flex-col flex-1">
        {inner}
      </Link>
    </motion.div>
  );
};

export default function InsightsPage() {
  const [posts, setPosts] = useState(null);

  useDocumentMeta({
    title: "Insights",
    description:
      "Thought leadership and perspectives from the AMADI team on corporate structuring, compliance, and cross-border matters.",
    path: "/insights",
  });

  useEffect(() => {
    let active = true;
    api
      .get("/api/insights", { params: { t: Date.now() }, withCredentials: false })
      .then(({ data }) => active && setPosts(data))
      .catch(() => active && setPosts([]));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div data-testid="insights-page">
      <PageHero
        eyebrow="Insights"
        title="Insights"
        subtitle="Thought leadership and perspectives from the Amadi team."
        current="Insights"
      />

      <section data-testid="insights-grid-section" className="bg-canvas py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {posts === null && (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 text-charcoal/40 animate-spin" />
            </div>
          )}

          {posts && posts.length === 0 && (
            <div data-testid="insights-empty" className="text-center py-16">
              <p className="font-serif text-xl text-charcoal/55">
                New insights are on the way. Please check back soon.
              </p>
            </div>
          )}

          {posts && posts.length > 0 && (
            <>
              <FeaturedInsightCard post={posts[0]} />
              {posts.length > 1 && (
                <div
                  className="grid grid-cols-1 md:grid-cols-3 gap-px bg-charcoal/12 border border-charcoal/12"
                >
                  {posts.slice(1).map((post) => (
                    <InsightCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
